import autoTable from "jspdf-autotable";
import { deriveCallReviewSections } from "./callReviewScoring";

// Mirrors CallReviewTable.css colors so the native PDF table matches the
// on-screen table it replaces.
const HEADER_FILL = [31, 93, 117]; // #1f5d75 (teal)
const HEADER_TEXT = [255, 255, 255];
const ROW_FILL = [238, 241, 244]; // #eef1f4
const ROW_ALT_FILL = [227, 231, 235]; // #e3e7eb
const TOTAL_ROW_FILL = [214, 221, 227]; // #d6dde3
const BORDER_COLOR = [208, 215, 222]; // #d0d7de
const TEXT_COLOR = [51, 51, 51]; // #333

const BASE_STYLES = {
  fontSize: 9,
  cellPadding: 3,
  overflow: "linebreak", // wrap long "Scoring Category" text instead of clipping it
  valign: "middle",
  lineColor: BORDER_COLOR,
  lineWidth: 0.2,
  textColor: TEXT_COLOR,
};

const HEAD_STYLES = {
  fillColor: HEADER_FILL,
  textColor: HEADER_TEXT,
  fontStyle: "bold",
  halign: "left",
};

// Marks a row as the bold summary "Total"/"Average" row so didParseCell can
// give it the highlighted background regardless of table.
const markTotalRow = (rowIndex, totalRowIndexes) => totalRowIndexes.has(rowIndex);

/**
 * Renders the Call Review Sheet's scoring tables (summary + the three detail
 * sections) as native, vector jsPDF-AutoTable tables instead of a
 * html2canvas screenshot. AutoTable natively handles what a raster capture
 * can't: it never splits a row across a page break, it repeats the header
 * row on every page it spans, it wraps long cell text instead of clipping
 * it, and it fits within the printable page width automatically.
 *
 * Returns the Y position (mm) directly below the last table drawn, so the
 * caller can continue laying out content beneath it.
 */
export const renderCallReviewAutoTables = ({
  doc,
  startY,
  margin = 14,
  callReviewData,
  onPage,
}) => {
  const derived = deriveCallReviewSections(callReviewData);
  if (!derived) return startY;

  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - margin * 2;

  const { utSection, sSection, oSection, totals, average, totalScore5 } =
    derived.summary;

  const pct = (v) => `${(v ?? 0).toFixed(1)}%`;
  const num = (v) => (v ?? 0).toFixed(2);

  const summaryBody = [
    ["Using the Tools", utSection.max ?? 0, num(utSection.achieved), num(utSection.outOf5), pct(utSection.percentage)],
    ["Sales", sSection.max ?? 0, num(sSection.achieved), num(sSection.outOf5), pct(sSection.percentage)],
    ["Objections", oSection.max ?? 0, num(oSection.achieved), num(oSection.outOf5), pct(oSection.percentage)],
    ["Total", totals.max ?? 0, num(totals.achieved), totalScore5.toFixed(2), ""],
    ["Average Score", "", "", num(average.outOf5), `${(average.percentage ?? 0).toFixed(2)}%`],
  ];
  const summaryTotalRows = new Set([3, 4]);

  let cursorY = startY;

  autoTable(doc, {
    startY: cursorY,
    margin: { left: margin, right: margin, top: margin, bottom: margin },
    head: [["Scoring Category", "Max Score", "Achieved Score", "Score out of 5", "% Score"]],
    body: summaryBody,
    theme: "grid",
    styles: BASE_STYLES,
    headStyles: HEAD_STYLES,
    bodyStyles: { fillColor: ROW_FILL },
    alternateRowStyles: { fillColor: ROW_ALT_FILL },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.32 },
    },
    tableWidth: contentWidth,
    showHead: "everyPage",
    didParseCell: (data) => {
      if (data.section === "body" && markTotalRow(data.row.index, summaryTotalRows)) {
        data.cell.styles.fillColor = TOTAL_ROW_FILL;
        data.cell.styles.fontStyle = "bold";
      }
    },
    didDrawPage: (data) => {
      if (typeof onPage === "function") onPage(data);
    },
  });
  cursorY = doc.lastAutoTable.finalY + 10;

  const detailSections = [derived.usingTools, derived.sales, derived.objections];

  // Minimum vertical room (mm) required to start a new section on the
  // current page: title + header row + at least one (possibly wrapped)
  // body row. If this doesn't fit, the title and header would otherwise be
  // orphaned alone at the bottom of the page, with AutoTable pushing every
  // actual row to the next page and repeating the header there — i.e. the
  // header appearing to "come twice" with nothing under the first copy.
  const MIN_SECTION_SPACE_MM = 45;

  detailSections.forEach((section) => {
    // Section sub-title — force a fresh page unless there's enough room for
    // the title AND the header AND at least one row, not just the title.
    if (cursorY + MIN_SECTION_SPACE_MM > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      cursorY = margin;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...TEXT_COLOR);
    doc.text(section.title, margin, cursorY);
    cursorY += 5;

    const totalMax = section.maxValues.reduce((s, v) => s + (Number(v) || 0), 0);
    const totalAchieved = section.values.reduce(
      (s, v, i) => s + (v ?? 0) * (section.weightageFactor[i] ?? 1),
      0,
    );

    const body = section.fields.map((label, idx) => [
      label,
      section.values[idx] ?? 0,
      (section.weightageFactor[idx] ?? 1).toFixed(2),
      (section.maxValues[idx] ?? 0).toFixed(2),
      ((section.values[idx] ?? 0) * (section.weightageFactor[idx] ?? 1)).toFixed(2),
    ]);
    body.push(["Total", "", "", totalMax.toFixed(2), totalAchieved.toFixed(2)]);
    const totalRowIndex = new Set([body.length - 1]);

    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin, top: margin },
      head: [["Scoring Category", "Score", "Weightage Factor", "Max Score", "Achieved Score"]],
      body,
      theme: "grid",
      styles: BASE_STYLES,
      headStyles: HEAD_STYLES,
      bodyStyles: { fillColor: ROW_FILL },
      alternateRowStyles: { fillColor: ROW_ALT_FILL },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.46 },
      },
      tableWidth: contentWidth,
      showHead: "everyPage",
      didParseCell: (data) => {
        if (data.section === "body" && markTotalRow(data.row.index, totalRowIndex)) {
          data.cell.styles.fillColor = TOTAL_ROW_FILL;
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: (data) => {
        if (typeof onPage === "function") onPage(data);
      },
    });

    cursorY = doc.lastAutoTable.finalY + 10;
  });

  return cursorY;
};

export default renderCallReviewAutoTables;
